import type { ComputedRef, WritableComputedRef } from 'vue';

import { SortOrder } from '~/apiClient';
import { FE_PAGE_PARAM } from '~/constants/pagination';
import { FE_SORT_PARAM, SORT_ORDER_DESC_PREFIX } from '~/constants/sorting';
import { type EnumValue } from '~/types/enum.type';

export interface SortingParams<T> {
    sortBy: T;
    sortOrder: SortOrder;
}

export type SortingModel<T> = WritableComputedRef<SortingParams<T> | undefined>;

interface SortingApiParams<T> {
    sortBy?: T;
    sortOrder?: SortOrder;
}

export interface UseSortingParamsReturn<T> {
    sorting: SortingModel<T>;
    sortingApiParams: ComputedRef<SortingApiParams<T>>;
}

export const useSortingParams = <EO extends Record<string, string>>(sortByEnum: EO): UseSortingParamsReturn<EnumValue<EO>> => {
    const route = useRoute();

    const sorting = computed<SortingParams<EnumValue<EO>> | undefined>({
        get: () => {
            const queryValue = locationQueryAsOptionalString(route.query[FE_SORT_PARAM]);
            const validateRegex = new RegExp(`^(${SORT_ORDER_DESC_PREFIX}?)(${Object.values(sortByEnum).join('|')})$`);
            const validateResult = queryValue && validateRegex.exec(queryValue);

            if (!validateResult) return undefined;

            const sortBy = validateResult[2] as EnumValue<EO>;
            const sortOrder: SortOrder = validateResult[1] === SORT_ORDER_DESC_PREFIX ? SortOrder.desc : SortOrder.asc;

            return { sortBy, sortOrder };
        },
        set(value) {
            const queryValue = value ? (value.sortOrder === SortOrder.desc ? SORT_ORDER_DESC_PREFIX : '') + value.sortBy : undefined;

            navigateTo({
                query: {
                    ...route.query,
                    [FE_SORT_PARAM]: queryValue,
                    [FE_PAGE_PARAM]: undefined,
                },
            });
        },
    });

    const sortingApiParams = computed<SortingApiParams<EnumValue<EO>>>(() => ({
        sortBy: sorting.value?.sortBy,
        sortOrder: sorting.value?.sortOrder,
    }));

    return {
        sorting,
        sortingApiParams,
    };
};
